import {
  ITEM_CALCULATED_FIELDS,
  INPUT_ITEM_CALCULATED_FIELDS,
} from "appConfig";

import { updateLineWithItem as updateFulfillmentLineWithItem } from "services/sosInventoryService/fulfillmentTransaction/domainLogic";
import {
  updateAdjustmentLineWithItem,
  updateTransferLineWithItem,
} from "services/sosInventoryService/inventoryTransaction/domainLogic";
import {
  updateLineWithItem as updateMoreLineWithItem,
  updateLineWithItemFromLocation as updateMoreLineItemFromLocation,
} from "services/sosInventoryService/moreTransaction/domainLogic";
import {
  updateLineWithItem as updateProductionLineWithItem,
  updateWorkOrderLineWithItem,
} from "services/sosInventoryService/productionTransaction/domainLogic";
import {
  updateReturnToVendorLineWithItem,
  updateLineWithItem as updatePurchasingLineWithItem,
} from "services/sosInventoryService/purchasingTransaction/domainLogic";
import { updateLineWithItem as updateSalesLineWithItem } from "services/sosInventoryService/salesTransaction/domainLogic";
import { getItemBom, getByIds } from "services/sosInventoryService/sosApi";
import { fetchDefaultBin } from "services/utility/bins";
import { dateToSosISODateTime } from "services/utility/dates";
import { setPageDirty } from "services/utility/edit";

import globalState from "globalState/globalState";
import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

import {
  OBJECT_TYPES,
  TO_FROM_LOCATION_OBJECT_TYPES,
  SALES_TRANSACTION_TYPES,
  NO_QUANTITY_MULTIPLY_OBJECT_TYPES,
} from "appConstants";

// Mapping of objectType to their respective updateLineWithItem functions
const UPDATE_LINE_WITH_ITEM_MAP = {
  [OBJECT_TYPES.SHIPMENT.fullString]: updateFulfillmentLineWithItem,
  [OBJECT_TYPES.PICK_TICKET.fullString]: updateFulfillmentLineWithItem,
  [OBJECT_TYPES.ADJUSTMENT.fullString]: updateAdjustmentLineWithItem,
  [OBJECT_TYPES.TRANSFER.fullString]: updateTransferLineWithItem,
  [OBJECT_TYPES.RENTAL.fullString]: updateMoreLineItemFromLocation,
  [OBJECT_TYPES.RENTAL_RETURN.fullString]: updateMoreLineItemFromLocation,
  [OBJECT_TYPES.RMA.fullString]: updateMoreLineWithItem,
  [OBJECT_TYPES.RETURN.fullString]: updateMoreLineWithItem,
  [OBJECT_TYPES.BUILD.fullString]: updateProductionLineWithItem,
  [OBJECT_TYPES.PROCESS.fullString]: updateProductionLineWithItem,
  [OBJECT_TYPES.WORK_ORDER.fullString]: updateWorkOrderLineWithItem,
  [OBJECT_TYPES.RETURN_TO_VENDOR.fullString]: updateReturnToVendorLineWithItem,
  [OBJECT_TYPES.INVOICE.fullString]: updateSalesLineWithItem,
  [OBJECT_TYPES.SALES_ORDER.fullString]: updateSalesLineWithItem,
  [OBJECT_TYPES.SALES_RECEIPT.fullString]: updateSalesLineWithItem,
  [OBJECT_TYPES.ESTIMATE.fullString]: updateSalesLineWithItem,
};

/**
 * Unified function to expand an item group (BOM) into individual component line items.
 * Uses objectType to determine which updateLineWithItem function to use.
 */
export async function expandItemGroup(
  lineToExpand,
  lineHandler,
  objectType,
  record,
  lineType = null,
  toLocation = null
) {
  globalState.dispatch(editModalLoadingIndicatorOn());
  const bom = await getItemBom(lineToExpand.item.id);
  const bomIds = bom.map((bomItem) => bomItem.componentItem.id);

  // Build params based on objectType
  const locationId = TO_FROM_LOCATION_OBJECT_TYPES.includes(objectType)
    ? record.fromLocation?.id
    : record.location?.id;

  // sales transactions use current available quantity regardless of
  // transaction date, so they don't need the dt (date/time) parameter
  const params = SALES_TRANSACTION_TYPES.includes(objectType)
    ? { locationId }
    : {
        locationId,
        dt: dateToSosISODateTime(record.date),
      };

  let calculatedFields = ITEM_CALCULATED_FIELDS[objectType];
  if (lineType === "input") {
    calculatedFields = INPUT_ITEM_CALCULATED_FIELDS[objectType];
    // build and process input lines need onhand when the setting is enabled
    const { showQuantityOnBuildsAndShipments } =
      globalState.getState().userCompanySettings.settings;
    if (showQuantityOnBuildsAndShipments && calculatedFields) {
      calculatedFields = ["onhand", ...calculatedFields];
    }
  }

  const data = await getByIds("item", bomIds, calculatedFields, params);
  lineHandler({ type: "delete", deleteAt: lineToExpand.lineNumber });

  // Get the appropriate updateLineWithItem function for this objectType
  const updateLineWithItem = UPDATE_LINE_WITH_ITEM_MAP[objectType];

  const lines = await Promise.all(
    bom.map(async (bomItem, i) => {
      const item = data.find(({ id }) => id === bomItem.componentItem.id);
      if (!item) {
        return null;
      }

      // Some object types (like adjustments) don't multiply the quantity
      const initialLine = NO_QUANTITY_MULTIPLY_OBJECT_TYPES.includes(objectType)
        ? { ...lineToExpand }
        : {
            ...lineToExpand,
            quantity: bomItem.quantity.times(lineToExpand.quantity),
          };
      delete initialLine.id; // remove id to avoid copying over old line's id

      // remove all bin data to avoid copying over old line's bin
      delete initialLine.bin;
      delete initialLine.fromBin;

      const newLine = await updateLineWithItem(
        item,
        initialLine,
        record,
        lineType // pass lineType for transactions that require special handling of bins based on whether it's an input or output line
      );

      // set toBin for object types that have a bin tracked toLocation
      if (TO_FROM_LOCATION_OBJECT_TYPES.includes(objectType)) {
        newLine.toBin = toLocation?.binTracking
          ? await fetchDefaultBin(item.id, toLocation?.id)
          : null;
      }

      // Sales transactions need the amount field calculated
      const lineWithAmount = SALES_TRANSACTION_TYPES.includes(objectType)
        ? {
            ...newLine,
            amount: newLine.quantity.times(newLine.unitprice),
          }
        : newLine;

      return {
        ...lineWithAmount,
        lineNumber: lineToExpand.lineNumber + i,
      };
    })
  );

  setPageDirty();
  lineHandler({
    type: "insertMany",
    insertAt: lineToExpand.lineNumber,
    lines: lines.filter((line) => line),
  });
  globalState.dispatch(editModalLoadingIndicatorOff());
}

/**
 * Expands an item group (BOM) into individual component line items for
 * inbound purchasing transactions (purchase orders and item receipts).
 */
export async function expandInboundPurchasingItemGroup(
  lineToExpand,
  objectType,
  record
) {
  globalState.dispatch(editModalLoadingIndicatorOn());
  const bom = await getItemBom(lineToExpand.item.id);
  const bomIds = bom.map((bomItem) => bomItem.componentItem.id);

  const data = await getByIds(
    "item",
    bomIds,
    ITEM_CALCULATED_FIELDS[objectType],
    { locationId: record.location?.id }
  );

  const lines = await Promise.all(
    bom.map(async (bomItem, index) => {
      const item = data.find(({ id }) => id === bomItem.componentItem.id);
      if (!item) {
        return null;
      }
      const initialLine = {
        ...lineToExpand,
        quantity: bomItem.quantity.times(lineToExpand.quantity),
      };
      delete initialLine.id; // remove id to avoid copying over old line's id
      const newLine = await updatePurchasingLineWithItem(
        item,
        initialLine,
        record.vendor
      );
      return { ...newLine, lineNumber: lineToExpand.lineNumber + index };
    })
  );
  globalState.dispatch(editModalLoadingIndicatorOff());
  return lines.filter((line) => line);
}
